// Local report exporters — Excel (xlsx) + PDF (jsPDF).
// All generation is 100% client-side.

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function fmtPct(numerator, total) {
  if (!total) return '0.000';
  return ((numerator / total) * 100).toFixed(3);
}

function ts() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

/**
 * Write an .xlsx workbook with:
 *   Sheet 1 (Summary): filenames, page counts, totals
 *   Sheet 2 (Pages):   per-page stats
 */
export function exportExcelReport(comparison, meta) {
  const { pages, page_count, file_a_pages, file_b_pages } = comparison;

  // Aggregate totals
  const totals = pages.reduce(
    (acc, p) => {
      const s = p.stats || { total: 0, overlap: 0, only_a: 0, only_b: 0 };
      acc.total += s.total;
      acc.overlap += s.overlap;
      acc.only_a += s.only_a;
      acc.only_b += s.only_b;
      return acc;
    },
    { total: 0, overlap: 0, only_a: 0, only_b: 0 }
  );

  const wb = XLSX.utils.book_new();

  // Summary
  const summary = [
    ['Document Overlay Comparison — Summary'],
    [],
    ['File A', meta.file_a_name || 'File A'],
    ['File B', meta.file_b_name || 'File B'],
    ['File A pages', file_a_pages],
    ['File B pages', file_b_pages],
    ['Pages compared', page_count],
    ['Generated', new Date().toISOString()],
    [],
    ['Totals (pixels)'],
    ['Overlap (both)', totals.overlap],
    ['File A only', totals.only_a],
    ['File B only', totals.only_b],
    ['Total pixels', totals.total],
    ['Overall diff %', fmtPct(totals.only_a + totals.only_b, totals.total)],
  ];
  const summaryWs = XLSX.utils.aoa_to_sheet(summary);
  summaryWs['!cols'] = [{ wch: 22 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  // Pages
  const header = [
    'Page',
    'Width',
    'Height',
    'Total pixels',
    'Overlap',
    'File A only',
    'File B only',
    'Overlap %',
    'A only %',
    'B only %',
    'Diff %',
  ];
  const rows = pages.map((p, i) => {
    const s = p.stats || { total: 0, overlap: 0, only_a: 0, only_b: 0 };
    return [
      i + 1,
      p.width,
      p.height,
      s.total,
      s.overlap,
      s.only_a,
      s.only_b,
      Number(fmtPct(s.overlap, s.total)),
      Number(fmtPct(s.only_a, s.total)),
      Number(fmtPct(s.only_b, s.total)),
      Number(fmtPct(s.only_a + s.only_b, s.total)),
    ];
  });
  const pageWs = XLSX.utils.aoa_to_sheet([header, ...rows]);
  pageWs['!cols'] = [
    { wch: 6 },
    { wch: 8 },
    { wch: 8 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
  ];
  XLSX.utils.book_append_sheet(wb, pageWs, 'Pages');

  XLSX.writeFile(wb, `comparison-report-${ts()}.xlsx`);
}

function loadImg(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });
}

// Composite base + overlay for a page into a single canvas (for PDF embedding)
async function renderPageComposite(page) {
  const [base, overlay] = await Promise.all([
    loadImg(`data:image/png;base64,${page.base}`),
    loadImg(`data:image/png;base64,${page.overlay}`),
  ]);
  const canvas = document.createElement('canvas');
  canvas.width = base.width;
  canvas.height = base.height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(base, 0, 0);
  ctx.drawImage(overlay, 0, 0);
  return canvas;
}

/**
 * Write a multi-page PDF report:
 *   Page 1 — Cover with metadata, legend, and per-page stats table
 *   Pages 2..N — One page per comparison page: composite image + stats
 */
export async function exportPdfReport(comparison, meta) {
  const { pages, page_count, file_a_pages, file_b_pages } = comparison;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Cover
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Document Overlay Comparison', 40, 60);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const meta1 = [
    `File A: ${meta.file_a_name || 'File A'}   (${file_a_pages} page${file_a_pages !== 1 ? 's' : ''})`,
    `File B: ${meta.file_b_name || 'File B'}   (${file_b_pages} page${file_b_pages !== 1 ? 's' : ''})`,
    `Pages compared: ${page_count}`,
    `Generated: ${new Date().toLocaleString()}`,
  ];
  meta1.forEach((line, i) => doc.text(line, 40, 92 + i * 16));

  // Legend
  const legendY = 170;
  doc.setFontSize(10);
  doc.text('Legend', 40, legendY);
  const legend = [
    { color: [0, 0, 0], label: 'Overlap (present in both)' },
    { color: [239, 68, 68], label: 'File A only' },
    { color: [34, 197, 94], label: 'File B only' },
  ];
  legend.forEach((l, i) => {
    const y = legendY + 14 + i * 16;
    doc.setFillColor(l.color[0], l.color[1], l.color[2]);
    doc.rect(40, y - 9, 10, 10, 'F');
    doc.setTextColor(0, 0, 0);
    doc.text(l.label, 58, y);
  });

  // Table
  const tableStart = legendY + 14 + legend.length * 16 + 18;
  autoTable(doc, {
    startY: tableStart,
    head: [['Page', 'W', 'H', 'Overlap', 'A only', 'B only', 'Diff %']],
    body: pages.map((p, i) => {
      const s = p.stats || { total: 0, overlap: 0, only_a: 0, only_b: 0 };
      return [
        i + 1,
        p.width,
        p.height,
        s.overlap.toLocaleString(),
        s.only_a.toLocaleString(),
        s.only_b.toLocaleString(),
        fmtPct(s.only_a + s.only_b, s.total) + '%',
      ];
    }),
    headStyles: { fillColor: [9, 9, 11], textColor: 255 },
    styles: { fontSize: 9, cellPadding: 4 },
    theme: 'grid',
    margin: { left: 40, right: 40 },
  });

  // Per-page images
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    doc.addPage();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`Page ${i + 1} of ${page_count}`, 40, 40);

    // eslint-disable-next-line no-await-in-loop
    const canvas = await renderPageComposite(page);
    const imgData = canvas.toDataURL('image/png');
    const maxW = W - 80;
    const maxH = H - 130;
    const ratio = Math.min(maxW / canvas.width, maxH / canvas.height);
    const w = canvas.width * ratio;
    const h = canvas.height * ratio;
    doc.addImage(imgData, 'PNG', 40, 60, w, h);

    const s = page.stats || { total: 0, overlap: 0, only_a: 0, only_b: 0 };
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(
      `Overlap: ${s.overlap.toLocaleString()}   |   File A only: ${s.only_a.toLocaleString()}   |   File B only: ${s.only_b.toLocaleString()}   |   Diff: ${fmtPct(
        s.only_a + s.only_b,
        s.total
      )}%`,
      40,
      H - 30
    );
  }

  doc.save(`comparison-report-${ts()}.pdf`);
}
